<template>
  <div
    class="fts-automation-node-for-each automation-node"
    :class="{
      'automation-node--debug': data.debug,
      'automation-node--debugging': isDebugging,
    }"
  >
    <fts-automation-block-number :data="data" />

    <div class="node-content">
      <div class="node-content-icon node-content-icon--for">
        <i class="fts-icon-automation-for"></i>
      </div>

      <div class="node-content-wrapper">
        <!-- automation-blocks-simplification P8: "for" a list, or "repeat" a number of times -->
        <div
          class="node-for-each-label node-for-each-mode"
          :title="isRepeat() ? 'Repeat a number of times (click to loop over a list)' : 'Loop over a list (click to repeat a number of times)'"
          @click="toggleRepeat"
        >{{ isRepeat() ? "repeat" : "for" }}</div>

        <fts-formula
          v-model="data.list"
          :variables="priorVariables"
          class="node-for-each-formula"
          @change="validateFormula"
          @input="updateData"
        />
        <div class="node-for-each-label node-for-each-times" v-if="isRepeat()">times</div>
      </div>

      <div class="node-content-debug">
        <fts-automation-debugpoint
          v-model="data.debug"
          class="node-header__debugpoint"
          @input="updateData"
        />
      </div>
    </div>

    <div class="node-for-each-sub">
      <div class="node-for-each-label">each</div>

      <div class="node-for-each-sub-group">
        <i
          :class="informationIcon(data.variableType)"
          class="node-for-each-sub-icon"
        ></i>

        <fts-automation-contenteditable 
          placeholder="type row's name"
          v-model="data.item"
          @input="updateData"
        />
      </div>

      <div class="node-for-each-count" v-if="debugInformation">
        {{debugInformation}}
      </div>
    </div>

    <!-- automation-blocks-simplification P4: loop only over the items that meet a condition -->
    <div class="node-for-each-sub" v-if="hasWhere()">
      <div class="node-for-each-label">where</div>
      <fts-formula
        v-model="data.where"
        :variables="whereVariables()"
        class="node-for-each-formula"
        @input="updateData"
      />
      <i class="fts-icon-close node-for-each-where-remove" title="Remove the condition" @click="removeWhere"></i>
    </div>
    <div class="node-for-each-sub node-for-each-links" v-else-if="!isRepeat()">
      <span class="node-for-each-link" @click="addWhere">+ where</span>
    </div>

    <fts-automation-badge
      :module-id="data.moduleId"
      :activity-id="data.activityId"
      :information-id="data.informationId"
      :isGroup="data.isGroup"
      class="node-for-each-badge"
      v-if="data.moduleId || data.activityId || data.informationId"
    />
  </div>
</template>

<script>
import Vue from "vue";

import { automationDesignStore } from "../../store/automationDesignStore";
import commons from "../../commons/commons";
import FTSFormula from "../../design/FTSFormula.vue";
import FTSAutomationDebugpoint from "./FTSAutomationDebugpoint";
import FTSAutomationBlockNumber from "./FTSAutomationBlockNumber";
import FTSAutomationContenteditable from "./FTSAutomationContenteditable";
import FTSAutomationBadge from "./FTSAutomationBadge";

export default {
  props: {
    data: Object,
    information: Array,
  },
  data() {
    return {
      show: -1,
      content: "",
      formData: {},
      errorMessage: null,
      isDebugging: false,
      debugInformation: null,
      priorVariables: []
    };
  },
  watch: {
    "data.item": {
      handler(val) {
        if (val) {
          if (!this.data.itemFieldName) {
            this.data.itemFieldName = commons.generateFieldName();
          }
          // P8: the position chip "{item} #" (counting from 1) comes with every named item
          if (!this.data.positionFieldName) {
            Vue.set(this.data, "positionFieldName", commons.generateFieldName());
          }
          automationDesignStore.dispatch("updateVariables");
        }
      },
      immediate: true,
    }
  },
  components: {
    "fts-formula": FTSFormula,
    "fts-automation-debugpoint": FTSAutomationDebugpoint,
    "fts-automation-block-number": FTSAutomationBlockNumber,
    "fts-automation-contenteditable": FTSAutomationContenteditable,
    "fts-automation-badge": FTSAutomationBadge
  },
  methods: {
    /* methods, not computeds: the diagram's node data isn't reactive, so a cached computed never
       sees a toggle; updateData's $forceUpdate re-runs these on every redraw */
    isRepeat() {
      return this.data.mode === "repeat";
    },
    hasWhere() {
      return !this.isRepeat() && typeof this.data.where === "string";
    },
    // the where condition reads the item, which the loop's own variable list does not hold yet
    whereVariables() {
      const item = {
        id: this.data.item,
        name: this.data.item,
        fieldName: this.data.itemFieldName,
        nodeId: this.data.id,
        type: this.data.variableType,
        moduleId: this.data.moduleId,
        informationId: this.data.informationId,
      };
      return this.data.item ? [...this.priorVariables, item] : this.priorVariables;
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    updateData() {
      this.$forceUpdate();
    },
    /* For each options, saved on the node as ForEachOptions reads them: mode ("repeat" or absent) and
       where (a condition on the item, absent when unset). Repeat has no where. */
    toggleRepeat() {
      if (this.isRepeat()) {
        Vue.delete(this.data, "mode");
      } else {
        Vue.set(this.data, "mode", "repeat");
        Vue.delete(this.data, "where");
      }
      this.validateFormula();
      this.updateData();
    },
    addWhere() {
      Vue.set(this.data, "where", "");
      this.updateData();
    },
    removeWhere() {
      Vue.delete(this.data, "where");
      this.updateData();
    },
    async validateFormula() {
      let vm = this;
      vm.errorMessage = null;
      let diagram = _.cloneDeep(automationDesignStore.state.diagram);
      try {
        let result = await this.$store.dispatch("validateAutomation", {
          blocks: diagram,
          validate: {
            nodeId: this.data.id,
          },
        });

        if (!this.data.itemFieldName) {
          this.data.itemFieldName = commons.generateFieldName();
        }

        let returnedNode = Object.values(result.data.blocks).find(
          (x) => x.data.id == this.data.id
        );
        Vue.set(this.data, "variableType", returnedNode.data.variableType);

        if (returnedNode.data.moduleId)
          Vue.set(this.data, "moduleId", returnedNode.data.moduleId);
        else {
          Vue.set(this.data, "moduleId", null);
        }

        if (returnedNode.data.informationId)
          Vue.set(this.data, "informationId", returnedNode.data.informationId);
        else {
          Vue.set(this.data, "informationId", null);
        }

        if (returnedNode.data.isGroup)
          Vue.set(this.data, "isGroup", true);
        else {
          Vue.set(this.data, "isGroup", null);
        }

        automationDesignStore.dispatch("updateVariables");
        window.eventBus.$emit("refreshAutomationBlocks");
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          vm.errorMessage = err.response.data.message;
        } else {
          vm.errorMessage = "Something went wrong";
        }
      }
    },
    refreshBlockState() {
      this.isDebugging = false;
      if (
        automationDesignStore.state.debugPayload &&
        automationDesignStore.state.debugPayload.nodeId
      ) {
        let nodeId = automationDesignStore.state.debugPayload.nodeId;
        let diagram = automationDesignStore.state.diagram;
        let forEach = automationDesignStore.state.debugPayload.forEach;
        /* the debugged node may have been deleted since the payload was captured */
        if (diagram && diagram[nodeId] && diagram[nodeId].data.id == this.data.id) {
          this.isDebugging = true;
        }

        let currentNode = diagram && Object.values(diagram).find(
          (x) => x.data.id == this.data.id
        );
        var forEachLine = currentNode && (forEach || []).find((x) => x.nodeId == currentNode.id);
        if (forEachLine) {
          this.debugInformation = `${forEachLine.index + 1} of ${
            forEachLine.count
          }`;
        } else {
          this.debugInformation = "";
        }
      } else {
        this.debugInformation = "";
      }

      try {
        this.priorVariables = this.data.variables.filter((x) => x.id !== this.data.item);
      } catch (ex) {
        this.priorVariables = [];
      }

      this.$forceUpdate();
    },
  },
  mounted() {
    window.eventBus.$on("refreshAutomationBlocks", this.refreshBlockState);
  },
  beforeDestroy() {
    /* this handler only — a bare $off would strip every other block's listener too */
    window.eventBus.$off("refreshAutomationBlocks", this.refreshBlockState);
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-automation-node-for-each {
  padding: 0.75rem;
  outline: 2px solid rgba(var(--fts-block-for-background-rgb), 0.4);

  .node-content-wrapper {
    display: flex;
    align-items: flex-start;
    flex: 1;
  }
  .node-for-each-label {
    margin-right: 0.25rem;
    color: var(--fts-default);
  }
  .node-for-each-formula {
    flex: 1;
  }
  .node-for-each-count {
    margin-left: 0.5rem;
    flex-shrink: 0;
    color: var(--fts-default);
  }
  .node-for-each-badge {
    margin-top: 0.25rem;
    margin-left: 1.375rem;
    color: var(--fts-default);
  }
  .node-for-each-sub {
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
    margin-left: 1.375rem;
  }
  .node-for-each-sub-group {
    display: flex;
    align-items: center;
    flex: 1;
  }
  .node-for-each-sub-icon {
    margin-right: 0.375rem;
  }
  .node-for-each-mode {
    cursor: pointer;
    text-decoration: underline dotted;
  }
  .node-for-each-times {
    margin-left: 0.25rem;
    margin-right: 0;
  }
  .node-for-each-link {
    cursor: pointer;
    color: var(--fts-default);
    font-size: 0.75rem;
  }
  .node-for-each-where-remove {
    margin-left: 0.25rem;
    cursor: pointer;
    color: var(--fts-default);
  }
}
</style>